/**
 * IndexedDB Helper with expiration support and in-memory cache.
 */

/* eslint-disable no-console */

// IndexedDB configuration (legacy name retained for compatibility)
const DB_NAME = 'IndexedCache';
const STORE_NAME = 'CacheEntries';
const DB_VERSION = 1;
const SUPPORTS_INDEXED_DB = typeof indexedDB !== 'undefined';
const DEFAULT_TIME_LIMIT_MINUTES = 1;
const CACHE_TIME_LIMIT_MS = (() => {
	const timeLimitEnv = import.meta.env.REACT_APP_CACHE_TIME_LIMIT;
	const parsedLimit = parseFloat(timeLimitEnv || `${DEFAULT_TIME_LIMIT_MINUTES}`);
	const timeLimit = Number.isFinite(parsedLimit) ? parsedLimit : DEFAULT_TIME_LIMIT_MINUTES;
	return Math.max(timeLimit, 0) * 60000;
})();

// In-memory cache and initialization
const inMemoryCache = new Map<string, string>();
let dbPromise: Promise<IDBDatabase> | null = null;
let initializationPromise: Promise<void> | null = null;
let isCacheHydrated = false;

async function openIndexedDb(): Promise<IDBDatabase> {
	if (!SUPPORTS_INDEXED_DB) {
		throw new Error('IndexedDB is not supported in this environment');
	}

	if (!dbPromise) {
		dbPromise = new Promise((resolve, reject) => {
			const request = indexedDB.open(DB_NAME, DB_VERSION);

			request.onupgradeneeded = () => {
				const db = request.result;
				if (!db.objectStoreNames.contains(STORE_NAME)) {
					db.createObjectStore(STORE_NAME);
				}
			};

			request.onsuccess = () => {
				const db = request.result;
				db.onversionchange = () => {
					db.close();
					dbPromise = null;
				};
				db.onclose = () => {
					dbPromise = null;
				};
				resolve(db);
			};
			request.onerror = () => {
				dbPromise = null;
				reject(request.error || new Error('Failed to open IndexedDB'));
			};
			request.onblocked = () => {
				dbPromise = null;
				reject(new Error('IndexedDB open request was blocked'));
			};
		});
	}

	try {
		return await dbPromise;
	} catch (error) {
		dbPromise = null;
		throw error;
	}
}

async function ensureCacheHydrated(): Promise<void> {
	if (isCacheHydrated) return;
	if (!initializationPromise) {
		initializationPromise = hydrateCacheFromIndexedDb();
	}
	try {
		await initializationPromise;
		isCacheHydrated = true;
	} catch (error) {
		initializationPromise = null;
		throw error;
	}
}

function startHydration(): void {
	if (!initializationPromise) {
		initializationPromise = hydrateCacheFromIndexedDb()
			.then(() => {
				isCacheHydrated = true;
			})
			.catch((error) => {
				initializationPromise = null;
				if (process.env.NODE_ENV === 'development') {
					console.warn('Failed to hydrate IndexedDB cache, will retry on next access:', error);
				}
			});
	}
}

startHydration();

async function hydrateCacheFromIndexedDb(): Promise<void> {
	if (!SUPPORTS_INDEXED_DB) {
		return;
	}

	try {
		const db = await openIndexedDb();
		await new Promise<void>((resolve, reject) => {
			const transaction = db.transaction(STORE_NAME, 'readonly');
			const store = transaction.objectStore(STORE_NAME);
			const keysRequest = store.getAllKeys();
			const valuesRequest = store.getAll();
			let keys: string[] | null = null;
			let values: unknown[] | null = null;
			let settled = false;

			const hydrate = () => {
				if (settled || !keys || !values) return;
				const limit = Math.min(keys.length, values.length);
				for (let i = 0; i < limit; i += 1) {
					inMemoryCache.set(String(keys[i]), values[i] as string);
				}
				settled = true;
				resolve();
			};

			const onError = () => {
				if (settled) return;
				settled = true;
				reject(
					keysRequest.error ||
						valuesRequest.error ||
						transaction.error ||
						new Error('Failed to read IndexedDB during hydration'),
				);
			};

			keysRequest.onsuccess = () => {
				const rawKeys = keysRequest.result as
					| Array<string | number | Date | ArrayBuffer | ArrayBufferView>
					| null
					| undefined;
				if (rawKeys && Array.isArray(rawKeys)) {
					keys = rawKeys.map((k) => String(k));
				} else {
					keys = null;
				}
				hydrate();
			};
			valuesRequest.onsuccess = () => {
				values = valuesRequest.result as unknown[];
				hydrate();
			};

			keysRequest.onerror = onError;
			valuesRequest.onerror = onError;
			transaction.onerror = onError;
		});
	} catch (error) {
		if (process.env.NODE_ENV === 'development') {
			console.warn('Failed to hydrate IndexedDB cache:', error);
		}
		throw error;
	}
}

async function getFromIndexedDb(key: string): Promise<string | null> {
	if (!SUPPORTS_INDEXED_DB) {
		return null;
	}

	try {
		const db = await openIndexedDb();
		return await new Promise<string | null>((resolve, reject) => {
			const transaction = db.transaction(STORE_NAME, 'readonly');
			const store = transaction.objectStore(STORE_NAME);
			const request = store.get(key);

			request.onsuccess = () => resolve((request.result as string | undefined | null) ?? null);
			request.onerror = () => reject(request.error || new Error('IndexedDB get failed'));
			transaction.onerror = () =>
				reject(transaction.error || request.error || new Error('IndexedDB transaction failed during get'));
		});
	} catch (error) {
		if (process.env.NODE_ENV === 'development') {
			console.warn(`IndexedDB get failed for key '${key}':`, error);
		}
		return null;
	}
}

async function setToIndexedDb(key: string, value: string): Promise<void> {
	if (!SUPPORTS_INDEXED_DB) {
		return;
	}

	try {
		const db = await openIndexedDb();
		await new Promise<void>((resolve, reject) => {
			const transaction = db.transaction(STORE_NAME, 'readwrite');
			const store = transaction.objectStore(STORE_NAME);
			const request = store.put(value, key);

			transaction.oncomplete = () => resolve();
			transaction.onabort = () =>
				reject(transaction.error || request.error || new Error('IndexedDB transaction aborted during set'));
			transaction.onerror = () =>
				reject(transaction.error || request.error || new Error('IndexedDB transaction failed during set'));
		});
	} catch (error) {
		if (process.env.NODE_ENV === 'development') {
			console.warn(`IndexedDB set failed for key '${key}':`, error);
		}
	}
}

async function removeFromIndexedDb(keys: string[]): Promise<void> {
	if (!SUPPORTS_INDEXED_DB) {
		return;
	}

	try {
		const db = await openIndexedDb();
		await new Promise<void>((resolve, reject) => {
			const transaction = db.transaction(STORE_NAME, 'readwrite');
			const store = transaction.objectStore(STORE_NAME);

			for (const key of keys) {
				store.delete(key);
			}

			transaction.oncomplete = () => resolve();
			transaction.onabort = () => reject(transaction.error || new Error('IndexedDB transaction aborted during remove'));
			transaction.onerror = () => reject(transaction.error || new Error('IndexedDB transaction failed during remove'));
		});
	} catch (error) {
		if (process.env.NODE_ENV === 'development') {
			console.warn('IndexedDB remove failed:', error);
		}
	}
}

async function clearIndexedDb(): Promise<void> {
	if (!SUPPORTS_INDEXED_DB) {
		return;
	}

	try {
		const db = await openIndexedDb();
		await new Promise<void>((resolve, reject) => {
			const transaction = db.transaction(STORE_NAME, 'readwrite');
			const store = transaction.objectStore(STORE_NAME);
			const request = store.clear();

			transaction.oncomplete = () => resolve();
			transaction.onabort = () =>
				reject(transaction.error || request.error || new Error('IndexedDB transaction aborted during clear'));
			transaction.onerror = () =>
				reject(transaction.error || request.error || new Error('IndexedDB transaction failed during clear'));
		});
	} catch (error) {
		if (process.env.NODE_ENV === 'development') {
			console.warn('IndexedDB clear failed:', error);
		}
	}
}

function readFromCache(key: string): string | null {
	startHydration();
	return inMemoryCache.has(key) ? inMemoryCache.get(key)! : null;
}

async function readFromStorage(key: string): Promise<string | null> {
	const cached = readFromCache(key);
	if (cached !== null) {
		return cached;
	}

	const value = await getFromIndexedDb(key);
	if (value !== null) {
		inMemoryCache.set(key, value);
	}
	return value;
}

function cacheValue(key: string, value: string | null): void {
	if (value === null) {
		inMemoryCache.delete(key);
	} else {
		inMemoryCache.set(key, value);
	}
}

async function writeToStorage(key: string, value: string): Promise<void> {
	cacheValue(key, value);
	await setToIndexedDb(key, value);
}

function writeToStorageSync(key: string, value: string): void {
	cacheValue(key, value);
	setToIndexedDb(key, value).catch(() => {
		/* best-effort fire-and-forget */
	});
}

async function removeFromStorageAsync(keys: string[]): Promise<void> {
	keys.forEach((key) => cacheValue(key, null));
	await removeFromIndexedDb(keys);
}

function removeFromStorageSync(keys: string[]): void {
	keys.forEach((key) => cacheValue(key, null));
	removeFromIndexedDb(keys).catch(() => {
		/* best-effort cleanup */
	});
}

interface StorageItem {
	value: any;
	expiresAtMs: number;
	expiredAt?: string; // legacy field retained for backward compatibility
}

function resolveExpiresAtMs(item: StorageItem): number {
	if (Number.isFinite(item.expiresAtMs)) {
		return item.expiresAtMs;
	}
	if (item.expiredAt) {
		return Date.parse(item.expiredAt);
	}
	return NaN;
}

function isExpired(expiresAtMs: number): boolean {
	return !Number.isFinite(expiresAtMs) || Date.now() > expiresAtMs;
}

function createStorageItem(value: any): StorageItem {
	return {
		value,
		expiresAtMs: Date.now() + CACHE_TIME_LIMIT_MS,
	};
}

export function setToSession(value: any, name: string): void {
	startHydration();
	const item = createStorageItem(value);

	try {
		writeToStorageSync(name, JSON.stringify(item));
	} catch (storageError) {
		if (process.env.NODE_ENV === 'development') {
			console.warn(`Failed to set IndexedDB storage for item '${name}'. Continuing without caching.`, storageError);
		}
	}
}

export async function setToSessionAsync(value: any, name: string): Promise<void> {
	try {
		await ensureCacheHydrated();
	} catch (error) {
		if (process.env.NODE_ENV === 'development') {
			console.warn('Failed to hydrate cache before write, continuing best-effort:', error);
		}
	}
	const item = createStorageItem(value);

	try {
		await writeToStorage(name, JSON.stringify(item));
	} catch (storageError) {
		if (process.env.NODE_ENV === 'development') {
			console.warn(`Failed to set IndexedDB storage for item '${name}'. Continuing without caching.`, storageError);
		}
	}
}

async function readAndValidateItem<T>(name: string): Promise<T | null> {
	try {
		await ensureCacheHydrated();
	} catch (error) {
		if (process.env.NODE_ENV === 'development') {
			console.warn('Failed to hydrate cache before reading, continuing best-effort:', error);
		}
	}

	try {
		const item = await readFromStorage(name);
		if (!item) {
			return null;
		}

		const parsed: StorageItem = JSON.parse(item);
		const expiresAtMs = resolveExpiresAtMs(parsed);

		if (isExpired(expiresAtMs)) {
			await removeFromStorageAsync([name]);
			return null;
		}

		return parsed.value as T;
	} catch (error) {
		const errorMessage = error instanceof Error ? error.message : 'Unknown error';
		if (process.env.NODE_ENV === 'development') {
			console.warn(`Failed to parse storage item ${name}, removing corrupted item:`, errorMessage);
		}
		await removeFromStorageAsync([name]);
		return null;
	}
}

export async function checkExpiredStorageItemAsync<T>(name: string): Promise<T | null> {
	return readAndValidateItem<T>(name);
}

export function removeSessionItem(name: string): void {
	try {
		removeFromStorageSync([name]);
	} catch (error) {
		// Removal failures are non-fatal
	}
}

export function removeItemSessionStorageCache(name: string): void {
	removeSessionItem(name);
}

export function clearSessionStorageCache(): void {
	inMemoryCache.clear();
	clearIndexedDb().catch(() => {
		/* best-effort clear */
	});
}
